import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { getArticleListDataInner, getCategoryTopList, getThemeData } from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { isEmpty } from 'lodash-es';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { blogListJsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase } from '@/shared/path-helper';

const CategoryContent = dynamic(() => import('@/components/Category'), { ssr: true });

export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

  const { locale, req, resolvedUrl, query } = context;

  const { classifies = '' } = query;

  const themeData = await getThemeData(locale);

  const tempClassifies = isEmpty(classifies) ? themeData?.data?.classifies?.[0].k || '' : classifies;
  const [articleListRes, bookListRes, localeRes, tdkRes] = await Promise.all([
    getArticleListDataInner({ page_no: 1, page_size: 10, type: tempClassifies }, locale), // 列表
    getCategoryTopList({ type: tempClassifies }, locale), // 获取置顶文章
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const jsonLd = blogListJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: tdkRes?.data?.description ?? tempData['head_Meta_Desc_blogList'],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      url: canonicalUrl(req.headers.host),
    }
  );
  return {
    props: {
      ...localeRes,
      articleList: articleListRes || {},
      bookList: bookListRes?.data || [],
      classifies: themeData.data.classifies || [],
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title:
        tdkRes?.data?.title ??
        (tempClassifies !== 'Celebrity Follower Tracker'
          ? tempData['head_Meta_Title']
          : tempData['head_Meta_Title_blogList']),
      description:
        tdkRes?.data?.description ??
        (tempClassifies !== 'Celebrity Follower Tracker'
          ? tempData['head_Meta_Desc']
          : tempData['head_Meta_Desc_blogList']),
      jsonLd,
    },
  };
};

const CategoryPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  const getContent = useMemo(() => <CategoryContent data={props} />, [props]);
  return <>{getContent}</>;
};

CategoryPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
          <script
            async
            dangerouslySetInnerHTML={{
              __html: `
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "l9myh0u19b");
              `,
            }}
          />
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default CategoryPage;
